import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { PlusCircle, CalendarRange, MapPin, ExternalLink } from "lucide-react";
import { useTranslation as useTranslationOrig } from "react-i18next";

// --- i18n safe hook (évite TS2589) + helper string
type RawT = (key: string, options?: Record<string, unknown>) => any;
const useTranslationSafe = useTranslationOrig as unknown as () => { t: RawT };

type Tournament = {
  id: string;
  user_id: string;
  name: string;
  location: string | null;
  start_date: string | null;
  end_date: string | null;
  is_public: boolean;
  created_at?: string;
};

export default function TournamentHub() {
  // i18n
  const { t: tRaw } = useTranslationSafe();
  const tr = useCallback(
    (key: string, options?: Record<string, unknown>): string => {
      const out = tRaw(key, options);
      if (typeof out === "string") return out;
      if (Array.isArray(out)) return out.join("");
      return String(out ?? "");
    },
    [tRaw]
  );

  // Lists
  const [owned, setOwned] = useState<Tournament[]>([]);
  const [asAdmin, setAsAdmin] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id ?? null;

      if (!uid) {
        setLoading(false);
        return;
      }

      // 1) Mes tournois (owner)
      const { data: own } = await supabase
        .from("tournaments")
        .select("id,user_id,name,location,start_date,end_date,is_public,created_at")
        .eq("user_id", uid)
        .order("created_at", { ascending: false });

      // 2) Tournois où je suis admin
      const { data: adminLinks } = await supabase
        .from("tournament_admins")
        .select("tournament_id")
        .eq("user_id", uid);

      let adminTs: Tournament[] = [];
      const ids = (adminLinks || []).map((r) => r.tournament_id);
      if (ids.length) {
        const { data } = await supabase
          .from("tournaments")
          .select("id,user_id,name,location,start_date,end_date,is_public,created_at")
          .in("id", ids)
          .order("created_at", { ascending: false });
        adminTs = data || [];
      }

      setOwned(own || []);
      // éviter les doublons (owner + admin)
      const ownedIds = new Set((own || []).map((t) => t.id));
      setAsAdmin(adminTs.filter((t) => !ownedIds.has(t.id)));
      setLoading(false);
    })();
  }, []);

  const shell =
    "border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 rounded-xl";
  const textMain = "text-gray-900 dark:text-gray-100";
  const textDim = "text-gray-500 dark:text-gray-400";
  const btn =
    "inline-flex items-center gap-2 rounded-xl px-3 py-2 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900 transition";
  const card =
    "rounded-xl border border-gray-200 dark:border-gray-800 p-3 bg-white dark:bg-gray-950 space-y-2";

  const fmt = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString() : "—";

  return (
    <div className={`max-w-6xl mx-auto p-4 sm:p-6 space-y-8 ${textMain}`}>
      <header className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold">
          {tr("tournamentHub.title", { defaultValue: "Tournois" })}
        </h1>
        <Link to="/createtournament" className={btn}>
          <PlusCircle className="w-4 h-4" />
          {tr("tournamentHub.actions.new", { defaultValue: "Nouveau tournoi" })}
        </Link>
      </header>

      {/* Owned */}
      <section className={`${shell} p-4`}>
        <h2 className="font-bold mb-3">
          {tr("tournamentHub.owned.title", {
            defaultValue: "Mes tournois (propriétaire)",
          })}
        </h2>
        {loading ? (
          <div className={`text-sm ${textDim}`}>
            {tr("common.loading", { defaultValue: "Chargement…" })}
          </div>
        ) : owned.length === 0 ? (
          <div className={`text-sm ${textDim}`}>
            {tr("tournamentHub.owned.empty", {
              defaultValue: "Aucun tournoi créé pour l’instant.",
            })}
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {owned.map((t) => (
              <li key={t.id} className={card}>
                <div className="font-semibold">{t.name}</div>

                <div className={`text-xs flex items-center gap-2 ${textDim}`}>
                  <MapPin className="w-4 h-4" />
                  {t.location || "—"}
                </div>

                <div className={`text-xs flex items-center gap-2 ${textDim}`}>
                  <CalendarRange className="w-4 h-4" />
                  {fmt(t.start_date)}{" "}
                  {t.end_date ? `→ ${fmt(t.end_date)}` : ""}
                </div>

                <div className="flex gap-2 pt-1">
                  <Link to={`/tournaments/${t.id}`} className={`${btn} text-sm px-3 py-1`}>
                    {tr("tournamentHub.card.view", { defaultValue: "Voir" })}{" "}
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                  <Link to={`/tournaments/${t.id}/admin`} className={`${btn} text-sm px-3 py-1`}>
                    {tr("tournamentHub.card.admin", { defaultValue: "Admin" })}{" "}
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* As admin */}
      <section className={`${shell} p-4`}>
        <h2 className="font-bold mb-3">
          {tr("tournamentHub.adminOf.title", { defaultValue: "Où je suis admin" })}
        </h2>
        {loading ? (
          <div className={`text-sm ${textDim}`}>
            {tr("common.loading", { defaultValue: "Chargement…" })}
          </div>
        ) : asAdmin.length === 0 ? (
          <div className={`text-sm ${textDim}`}>
            {tr("tournamentHub.adminOf.empty", {
              defaultValue: "Aucun tournoi où vous êtes admin.",
            })}
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {asAdmin.map((t) => (
              <li key={t.id} className={card}>
                <div className="font-semibold">{t.name}</div>

                <div className={`text-xs flex items-center gap-2 ${textDim}`}>
                  <MapPin className="w-4 h-4" />
                  {t.location || "—"}
                </div>

                <div className={`text-xs flex items-center gap-2 ${textDim}`}>
                  <CalendarRange className="w-4 h-4" />
                  {fmt(t.start_date)}{" "}
                  {t.end_date ? `→ ${fmt(t.end_date)}` : ""}
                </div>

                <div className="flex gap-2 pt-1">
                  <Link to={`/tournaments/${t.id}`} className={`${btn} text-sm px-3 py-1`}>
                    {tr("tournamentHub.card.view", { defaultValue: "Voir" })}{" "}
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                  <Link to={`/tournaments/${t.id}/admin`} className={`${btn} text-sm px-3 py-1`}>
                    {tr("tournamentHub.card.admin", { defaultValue: "Admin" })}{" "}
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
